import { Chip, Divider, Grid, IconButton, Link, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import {
    BorderColorOutlined as EditIcon, FileCopyOutlined as FileCopyOutlinedIcon, FilterAlt as FilterAltIcon,
    Person as PersonIcon, SupervisedUserCircle as SupervisedUserCircleIcon, ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid';

import React, { memo, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import moment from 'moment';
import { useSnackbar } from 'notistack';

import { DataAPIContext } from '../api';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import PageMeta from '../components/PageMeta';
import { a11yProps } from '../components/TabPanel';
import { ContextStore } from '../store/contextStore';
import { copyTextToClipboard, LANGUAGES, useIsWidthUp } from '../utils';
import AccessGroupsPage from './agroups/AccessGroupsPage';
import UserNavigateToolbar from './UserNavigateToolbar';
import { LOCATION_STATUS } from './utils';
import UserListTab from './UserListTab';
import { onPretendToBeUser } from '../components/RecordListPro';
import useConfirmDialog from '../components/useConfirmDialog';
import { resetMfaAction, resetTwoStepSignIn } from './resetMfa';
import { grey } from '@mui/material/colors';
import AccessGroupsGridCell from '../components/AccessGroupsGridCell';


const useStyles = makeStyles((theme) => ({
    root: {
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
            marginTop: theme.spacing(2),
        },
    },
    gridRoot: {
        borderRadius: '15px',
        border: '1px solid #d8d8d8',
        borderBottom: 'none !important',
        outline: 'none !important',
        borderLeft: 'none !important',
        borderRight: 'none !important',
        [theme.breakpoints.down('sm')]: {
            border: 'none !important',
        },
    },
    tabsMenu: {
        paddingTop: '0.25rem',
        borderLeft: '1px solid #d8d8d8',
        borderRight: '1px solid #d8d8d8',
        borderRadius: '15px 15px 0 0',
        [theme.breakpoints.down('sm')]: {
            border: 'none !important',
            paddingTop: '0.5rem',
        },
    },
    chip: {
        borderRadius: '0px !important',
        marginRight: '0.25rem',
    },
}));

const getCurrentTab = (urlSearchParams, currentPath) => {
    if (currentPath.includes('permissions')) {
        return 2
    }
    const tab = parseInt(new URLSearchParams(urlSearchParams).get('tab'));
    // UserList has only tabs 0, 1, 2 - ignore invalid tab values from UserProfile
    if (tab && tab >= 0 && tab <= 2) {
        return tab;
    }
    return 0
}

// CP-8184: switching tabs unmounts the grid, and initialState-based visibility
// re-applied these defaults on every remount — so hidden columns reappeared.
// Control the model per tab and persist it, stored choices winning over defaults
// (same pattern as TripUserManager / AgentMetricsList).
const GUEST_COLUMN_VIS_KEY = 'UserList.columnVisibilityModel.guest';
const STAFF_COLUMN_VIS_KEY = 'UserList.columnVisibilityModel.staff';

const GUEST_DEFAULT_COLUMN_VISIBILITY = {
    id: false,
    first_name: true,
    email: true,
    phone: true,
    date_joined: false,
    is_app_installed: true,
    app_first_seen: true,
    is_email_verified: false,
    bypass_auth: false,
    allow_ads: false,
    tour_count: true,
    lang: false,
    location_status: false,
    notifications_status: false,
    latest_position: false,
    ota: true,
    atag: true,
    actions: true,
};

const readStoredColumnVisibility = (storageKey, defaults) => {
    try {
        const saved = window.localStorage.getItem(storageKey);
        if (saved) {
            return { ...defaults, ...JSON.parse(saved) };
        }
    } catch {
        /* quota / privacy mode / malformed */
    }
    return defaults;
};

// CP-7429: when the app was first seen for this user, sat right after "App
// installed" so the two read together. Identical on both tabs, so it is defined
// once rather than duplicated into each column array.
//
// The backend only started recording this on 2026-03-26 and only writes it the
// first time a platform is observed, so a user who installed before that has
// "App installed" = Yes and no date here, forever. Those two facts have to
// arrive together or the cell is worse than useless: a bare dash (or "No", or
// "Not recorded") next to an install reads as "no app", which is the opposite
// of the truth. Hence the explicit "Installed, date unknown" — it states the
// install and the gap in one go, so the cell survives being read on its own,
// without the neighbouring column and without the header tooltip.
//
// Not sortable/filterable on purpose: the value lives inside the `platform`
// JSONB blob with no index, and UserViewset's GRID_FILTER_ORDERING /
// GRID_FILTER_COLUMNS have no entry for it — an unmapped ordering key silently
// falls back to ordering by id, and an unmapped filter is refused (400), which the
// grid shows as its old, unfiltered rows, so offering either control would just lie
// to the user.
const APP_FIRST_SEEN_UNKNOWN = 'Installed, date unknown';

const appFirstSeenColumn = {
    field: 'app_first_seen',
    headerName: 'App downloaded',
    type: 'dateTime',
    // flex 1 to match date_joined: same date format, and at 0.6 it clipped.
    flex: 1,
    sortable: false,
    filterable: false,
    description: `When the app was first seen for this user. Recorded since 26 March 2026 — anyone who installed before then shows "${APP_FIRST_SEEN_UNKNOWN}".`,
    // A real Date, not a moment: DataGridPro v6's built-in dateTime valueFormatter
    // throws on anything that is not a Date, and getCellParams computes
    // formattedValue for EVERY cell whether or not renderCell is defined — so a
    // moment here takes the whole grid down with an uncaught error the moment one
    // row has a value. The other dateTime columns on this grid get away with a
    // moment only because each supplies its own valueFormatter, which shadows the
    // throwing built-in.
    valueGetter: (p) => (p.row.app_first_seen ? moment(p.row.app_first_seen).toDate() : null),
    valueFormatter: (p) => (p.value ? moment(p.value).format('MMMM DD, YYYY HH:mm:ss Z') : ''),
    // renderCell on top of valueFormatter: the empty case needs is_app_installed
    // off the row to tell "installed, but before we started recording" from
    // "no app at all", and valueFormatter is not given the row.
    renderCell: (p) => {
        if (p.value) {
            return p.formattedValue;
        }
        if (!p.row.is_app_installed) {
            return '-';
        }
        // Muted + italic so it does not scan as a recorded value at a glance.
        return (
            <Typography variant="body2" sx={{ color: grey[600], fontStyle: 'italic' }}>
                {APP_FIRST_SEEN_UNKNOWN}
            </Typography>
        );
    },
};

// Not sortable/filterable on purpose (CP-3420 follow-up): UserViewset has no
// GRID_FILTER_ORDERING / GRID_FILTER_COLUMNS entry for it, and a real one is costly.
// The time lives on User_Position, so a sort or a broad filter makes Postgres walk
// every user of a large operator for the few with a location (Crown Tours, 10/2026:
// ~1.25M users, 5,254 with one).
const lastLocationColumn = {
    field: 'latest_position',
    headerName: 'Last location reported',
    type: 'dateTime',
    flex: 0.5,
    sortable: false,
    filterable: false,
    valueGetter: (p) => (p.value ? moment(p.value.timestamp) : null),
    valueFormatter: (p) => (p.value ? p.value.format('MMMM DD, YYYY HH:mm:ss Z') : 'Never'),
};

const roleCellBackgroundColor = {
    10: '#D8D8D8',
    78: '#DAF3E5',
    11: '#E4E4F9',
};

const UserList = memo((props) => {
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const apiRef = useGridApiRef();
    const staffApiRef = useGridApiRef();
    const { roleMap, hasPerm, hasExplicitPerm, finalizeLogin, accessGroupsEnabled } = React.useContext(ContextStore);
    const [confirm, confirmDialog] = useConfirmDialog();
    const classes = useStyles();
    const location = useLocation();
    const resource = 'user';
    const [currentTab, setCurrentTab] = React.useState(getCurrentTab(location.search, location.pathname));
    const [syncActive, setSyncActive] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [query, setQuery] = React.useState(new URLSearchParams(location.search).get('query') || '');
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');
    const [navigateBackBarData, setNavigateBackBarData] = React.useState({ title: 'Users', addAction: () => history.push('/user/create'), buttonText: 'New user' })
    // The header's actions slot, held as state (not a ref) so the tab
    // re-renders and portals its Export button in once the slot exists.
    const [headerActionsEl, setHeaderActionsEl] = React.useState(null);
    const [recordListRoleFilters, setRecordListRoleFilters] = React.useState([]);
    const [hasActiveFutureBooking, setHasActiveFutureBooking] = React.useState(false);

    const [guestColumnVisibility, setGuestColumnVisibility] = React.useState(
        () => readStoredColumnVisibility(GUEST_COLUMN_VIS_KEY, GUEST_DEFAULT_COLUMN_VISIBILITY)
    );
    const [staffColumnVisibility, setStaffColumnVisibility] = React.useState(
        () => readStoredColumnVisibility(STAFF_COLUMN_VIS_KEY, {
            ...GUEST_DEFAULT_COLUMN_VISIBILITY,
            role: !accessGroupsEnabled(),
            agroups: accessGroupsEnabled(),
            ota: false,
        })
    );

    const onGuestColumnVisibilityModelChange = React.useCallback((model) => {
        setGuestColumnVisibility(model);
        try {
            window.localStorage.setItem(GUEST_COLUMN_VIS_KEY, JSON.stringify(model));
        } catch {
            /* quota / privacy mode */
        }
    }, []);

    const onStaffColumnVisibilityModelChange = React.useCallback((model) => {
        setStaffColumnVisibility(model);
        try {
            window.localStorage.setItem(STAFF_COLUMN_VIS_KEY, JSON.stringify(model));
        } catch {
            /* quota / privacy mode */
        }
    }, []);
    const cols = 12;

    React.useEffect(() => {
        const url_search_params = new URLSearchParams(location.search);
        if (!url_search_params.get('tab') && !location.pathname.includes("permissions")) {
            setCurrentTab(0);
            setNavigateBackBarData({ title: 'Users', addAction: () => history.push('/user/create'), buttonText: 'New user' });
        }
    }, [location.search, location.pathname]);

    // CP-6410: the delete runs as a background task (202). Refreshed when the task had only been queued, the
    // deleted user stayed listed, and each further click queued another delete of the same user. The rows are
    // hidden from the click on and the list refreshes once the task has finished.
    const [deletingIds, setDeletingIds] = React.useState([]);
    const hideDeletingRows = useMemo(() => {
        if (!deletingIds.length) return undefined;
        const hidden = new Set(deletingIds);
        return (rows) => rows.filter((row) => !hidden.has(row.id));
    }, [deletingIds]);
    const showRowsAgain = useCallback((ids) => {
        setDeletingIds((prev) => prev.filter((id) => !ids.includes(id)));
    }, []);

    const { deleteRecords } = useBatchDelete({
        resource,
        names: ['user', 'users'],
        // A task still running after the hook stops following it keeps its rows hidden; once it has
        // finished, the refreshed list shows whichever users it could not delete.
        onDeleted: ({ ids, finished }) => {
            if (finished) showRowsAgain(ids);
            setLastUpdate(new Date().toString());
        },
    });

    const onDeleteUser = useCallback(async (idlist) => {
        if (!idlist?.length) return;
        setDeletingIds((prev) => [...new Set([...prev, ...idlist])]);
        setSyncActive(true);
        const accepted = await deleteRecords(idlist);
        setSyncActive(false);
        if (!accepted) showRowsAgain(idlist);
    }, [deleteRecords, showRowsAgain]);

    const handleTabChange = useCallback((ev, newTab) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('page');
        searchParams.delete('pageSize');
        searchParams.set('tab', newTab);
        if (query) {
            searchParams.set('query', query);
        }
        if (newTab === 2) {
            setQuery(null);
            searchParams.delete('query')
        }
        props.setLastURLParams(searchParams.toString())
        if (newTab !== 2) {
            searchParams.delete('access_type')
        }
        history.push(`/user?${searchParams.toString()}`);
        if (newTab !== 2) {
            setNavigateBackBarData({ title: 'Users', addAction: () => history.push('/user/create'), buttonText: 'New user' });
        }
        setCurrentTab(newTab);
    }, [history, query, props.setLastURLParams]);

    const onQueryChange = useCallback((query) => {
        const searchParams = new URLSearchParams(location.search);
        setQuery(query);
        if (query) {
            searchParams.set('query', query);
        } else {
            searchParams.delete('query');
        }
        searchParams.set('tab', currentTab);
        props.setLastURLParams(searchParams.toString())
        history.push(`/user?${searchParams.toString()}`);
    }, [history, props.setLastURLParams]);

    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };

    const handleCopyToClipboard = useCallback((ev, value, label) => {
        ev.stopPropagation();
        copyToClipboard(value, label)
    }, [enqueueSnackbar]);

    const langOptions = useMemo(() => LANGUAGES.map((x) => ({ value: x.code, label: `${x.code} - ${x.name}` })), []);
    const locationOptions = useMemo(() => Object.keys(LOCATION_STATUS).map((x) => ({ value: +x, label: LOCATION_STATUS[x] })), []);
    const roleOptions = useMemo(() => Object.keys(roleMap).map((x) => ({ value: +x, label: roleMap[x].name })), [roleMap]);
    const notificationsOptions = useMemo(() => [
        { value: true, label: 'Enabled' },
        { value: false, label: 'Disabled' },
    ], []);

    const guestColumns = useMemo(() => [
        { field: 'id', headerName: 'ID', type: 'number', sortable: true, flex: 0.2, valueFormatter: (p) => p.value },
        { field: 'first_name', headerName: 'Full Name', sortable: true, flex: 1, valueGetter: (p) => p.row.display_name },
        {
            field: 'email', headerName: 'Email', sortable: true, flex: 1,
            renderCell: (p) => <>
                <Tooltip title="Copy email"><IconButton size="small" onClick={(ev) => handleCopyToClipboard(ev, p.row.email, 'Email')}><FileCopyOutlinedIcon fontSize="inherit" /></IconButton></Tooltip>
                <Link href={`mailto:${p.row.email}`}>{p.row.email}</Link>
            </>
        },
        {
            field: 'phone', headerName: 'Phone', sortable: false, flex: 1,
            renderCell: (p) => <>
                {p.row.phone ? <>
                    <Tooltip title="Copy phone"><IconButton size="small" onClick={(ev) => handleCopyToClipboard(ev, p.row.phone, 'Phone')}><FileCopyOutlinedIcon fontSize="inherit" /></IconButton></Tooltip>
                    <Link href={`tel:${p.row.phone}`}>{p.row.phone}</Link>
                </> : '-'}
            </>
        },
        {
            field: 'date_joined',
            headerName: 'Date Joined',
            sortable: true,
            flex: 1,
            type: 'dateTime',
            valueGetter: (p) => moment(p.row.date_joined),
            valueFormatter: (p) => {
                return p.value.format('MMMM DD, YYYY HH:mm:ss Z');
            },
        },
        { field: 'is_app_installed', headerName: 'App installed', type: 'boolean', flex: 0.3, renderCell: (p) => !!p.row.is_app_installed ? (p.row.latest_app_versions ? p.row.latest_app_versions : 'Yes') : 'No' },
        appFirstSeenColumn,
        { field: 'is_email_verified', headerName: 'Email verified', type: 'boolean', flex: 0.3, renderCell: (p) => !!p.row.is_email_verified ? 'Yes' : 'No' },
        { field: 'bypass_auth', headerName: 'Authentication disabled', type: 'boolean', flex: 0.3 },
        { field: 'allow_ads', headerName: 'Allow ads', type: 'boolean', flex: 0.3, renderCell: (p) => !!p.row.allow_ads ? 'Yes' : 'No' },
        { field: 'lang', headerName: 'Language', flex: 0.3, type: 'singleSelect', valueOptions: langOptions },
        { field: 'tour_count', headerName: 'Trip count', sortable: true, type: 'number', flex: 0.3 },
        { field: 'ota', headerName: 'OTA', sortable: false, flex: 0.3, valueGetter: (p) => p.row.ota?.name ?? '-' },
        { field: 'location_status', headerName: 'Location status', type: 'singleSelect', valueOptions: locationOptions, flex: 0.5 },
        { field: 'notifications_status', headerName: 'Notifications status', type: 'singleSelect', valueOptions: notificationsOptions, flex: 0.5 },
        lastLocationColumn,
        { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', ') },
        // Account-level tags (CP-8095) — facts about the PERSON, distinct from
        // both Access tags (RBAC scoping) and the per-booking tags.
        { field: 'tags', headerName: 'Tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.tags?.map((x) => (x.category ? `${x.category}: ${x.name}` : x.name)).join(', ') },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 150,
            renderHeader: (params) => {
                return (
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={6}>
                            <Typography variant="body2" fontWeight={500}>Actions</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Tooltip title="Filter">
                                <IconButton size="small" onClick={ev => apiRef.current?.showFilterPanel()}>
                                    <FilterAltIcon color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={3}>
                            <Tooltip title="Columns">
                                <IconButton size="small" onClick={ev => apiRef.current?.showPreferences("columns")}>
                                    <ViewColumnIcon color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>)
            },
            getActions: (params) => {
                const actions = [];
                hasPerm(resource, 'read') && actions.push(
                    <GridActionsCellItem
                        icon={<Tooltip title="User profile"><PersonIcon color="primary" /></Tooltip>}
                        label="User profile"
                        component={RouterLink}
                        to={`/user/${params.row.id}`}
                    />);
                hasPerm(resource, 'read') && actions.push(
                    <GridActionsCellItem
                        icon={<Tooltip title="Edit user"><EditIcon color="primary" /></Tooltip>}
                        label="Edit user"
                        component={RouterLink}
                        to={`/user/${params.row.id}/edit`}
                    />);
                return actions;
            }
        },
    ], [handleCopyToClipboard, langOptions, locationOptions, notificationsOptions, hasPerm, apiRef]);

    const staffColumns = useMemo(() => [
        { field: 'id', headerName: 'ID', type: 'number', sortable: true, flex: 0.2, valueFormatter: (p) => p.value },
        { field: 'first_name', headerName: 'Full Name', sortable: true, flex: 1, valueGetter: (p) => p.row.display_name },
        {
            field: 'role',
            headerName: 'Roles',
            sortable: false,
            editable: true,
            type: 'singleSelect',
            valueOptions: roleOptions,
            flex: 1,
            valueGetter: (p) => p.row.role,
            renderCell: (p) => {
                return (
                    <React.Fragment key={p.row.id}>
                        {(p.row.roles || []).map((roleId) => (
                            <Chip key={roleId} style={{ backgroundColor: roleCellBackgroundColor[roleId] }} size="small" label={roleMap[roleId].name} />
                        ))}
                    </React.Fragment>
                );
            },
        },
        {
            field: 'agroups',
            headerName: 'Access Groups',
            sortable: false,
            width: 150,
            renderCell: (p) => <AccessGroupsGridCell row={p.row} />,
        },
        { field: 'email', headerName: 'Email', sortable: true, flex: 1, renderCell: (p) => <Link href={`mailto:${p.row.email}`}>{p.row.email}</Link> },
        { field: 'phone', headerName: 'Phone', sortable: false, flex: 1, renderCell: (p) => <Link href={`tel:${p.row.phone}`}>{p.row.phone}</Link> || '-' },
        {
            field: 'date_joined',
            headerName: 'Date Joined',
            sortable: true,
            flex: 1,
            type: 'dateTime',
            valueGetter: (p) => moment(p.row.date_joined),
            valueFormatter: (p) => {
                return p.value.format('MMMM DD, YYYY HH:mm:ss Z');
            },
        },
        { field: 'is_app_installed', headerName: 'App installed', type: 'boolean', flex: 0.3, renderCell: (p) => !!p.row.is_app_installed ? (p.row.latest_app_versions ? p.row.latest_app_versions : 'Yes') : 'No' },
        appFirstSeenColumn,
        { field: 'is_email_verified', headerName: 'Email verified', type: 'boolean', flex: 0.3, valueGetter: (p) => !!p.value ? 'Yes' : 'No' },
        { field: 'bypass_auth', headerName: 'Authentication disabled', type: 'boolean', flex: 0.3 },
        { field: 'allow_ads', headerName: 'Allow ads', type: 'boolean', flex: 0.3, renderCell: (p) => !!p.row.allow_ads ? 'Yes' : 'No' },
        { field: 'lang', headerName: 'Language', flex: 0.3, type: 'singleSelect', valueOptions: langOptions },
        { field: 'tour_count', headerName: 'Trip count', sortable: true, type: 'number', flex: 0.3 },
        { field: 'ota', headerName: 'OTA', sortable: true, flex: 0.3, valueGetter: (p) => p.row.ota?.name ?? '-' },
        { field: 'location_status', headerName: 'Location status', type: 'singleSelect', valueOptions: locationOptions, flex: 0.5 },
        { field: 'notifications_status', headerName: 'Notifications status', type: 'singleSelect', valueOptions: notificationsOptions, flex: 0.5 },
        lastLocationColumn,

        { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', ') },
        // Account-level tags (CP-8095) — facts about the PERSON, distinct from
        // both Access tags (RBAC scoping) and the per-booking tags.
        { field: 'tags', headerName: 'Tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.tags?.map((x) => (x.category ? `${x.category}: ${x.name}` : x.name)).join(', ') },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 150,
            renderHeader: (params) => {
                return (
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={6}>
                            <Typography variant="body2" fontWeight={500}>Actions</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Tooltip title="Filter">
                                <IconButton size="small" onClick={ev => staffApiRef.current?.showFilterPanel()}>
                                    <FilterAltIcon color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={3}>
                            <Tooltip title="Columns">
                                <IconButton size="small" onClick={ev => staffApiRef.current?.showPreferences("columns")}>
                                    <ViewColumnIcon color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>)
            },
            getActions: (params) => {
                const actions = [];
                hasPerm(resource, 'read') && actions.push(
                    <GridActionsCellItem
                        icon={<Tooltip title="User profile"><PersonIcon color="primary" /></Tooltip>}
                        label="User profile"
                        component={RouterLink}
                        to={`/user/${params.row.id}`}
                    />);
                hasPerm(resource, 'read') && actions.push(
                    <GridActionsCellItem
                        icon={<Tooltip title="Edit user"><EditIcon color="primary" /></Tooltip>}
                        label="Edit user"
                        component={RouterLink}
                        to={`/user/${params.row.id}/edit`}
                    />);
                hasPerm(resource, 'pretend') && actions.push(
                    <GridActionsCellItem
                        icon={<Tooltip title="Impersonate user"><SupervisedUserCircleIcon color="primary" /></Tooltip>}
                        label="Impersonate user"
                        onClick={ev => onPretendToBeUser(ev, params.row, finalizeLogin, resource, dataAPI, enqueueSnackbar)}
                    />);
                const resetMfa = resetMfaAction({
                    row: params.row,
                    hasExplicitPerm,
                    onReset: (row) => resetTwoStepSignIn({ dataAPI, user: row, confirm, enqueueSnackbar }),
                });
                resetMfa && actions.push(resetMfa);
                return actions;
            }
        },
    ], [handleCopyToClipboard, langOptions, locationOptions, notificationsOptions, hasPerm, hasExplicitPerm, roleMap, finalizeLogin, staffApiRef, confirm]);

    return (
        <div className={classes.root}>
            <PageMeta title="Users" />
            {confirmDialog}
            <Grid container direction="row">
                <UserNavigateToolbar title={navigateBackBarData.title} buttonText={isSmallScreen ? null : navigateBackBarData.buttonText} buttonTooltip={navigateBackBarData.buttonText} addAction={navigateBackBarData.addAction} permission={navigateBackBarData.permission} goBackButtonAction={navigateBackBarData.goBackButtonAction} isDeleteButton={navigateBackBarData.isDeleteButton} actionsRef={setHeaderActionsEl} />
                <Grid item xs={12} sm={cols} className={classes.gridRoot}>
                    {!location.pathname.includes('permissions/') && <>
                        <Tabs className={classes.tabsMenu} value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" >
                            <Tab label="Guests" {...a11yProps(0)} />
                            <Tab label="Staff/guides" {...a11yProps(1)} />
                            {(hasPerm('agroup', 'list') || hasPerm('atag', 'list')) && <Tab label="Roles & Permissions" {...a11yProps(2)} />}
                        </Tabs>  <Divider /></>}

                    {currentTab === 0 && (
                        <UserListTab
                            isGuest={true}
                            recordListRoleFilters={recordListRoleFilters}
                            setRecordListRoleFilters={setRecordListRoleFilters}
                            hasActiveFutureBooking={hasActiveFutureBooking}
                            setHasActiveFutureBooking={setHasActiveFutureBooking}
                            currentTab={currentTab}
                            resource={resource}
                            onQueryChange={onQueryChange}
                            onDeleteUser={onDeleteUser}
                            transformRows={hideDeletingRows}
                            isSmallScreen={isSmallScreen}
                            query={query}
                            apiRef={apiRef}
                            lastUpdate={lastUpdate}
                            syncActive={syncActive}
                            headerActionsEl={hasPerm(resource, 'export') ? headerActionsEl : null}
                            columns={guestColumns}
                            columnVisibilityModel={guestColumnVisibility}
                            onColumnVisibilityModelChange={onGuestColumnVisibilityModelChange}
                            roleCellBackgroundColor={roleCellBackgroundColor}
                        />
                    )}
                    {currentTab === 1 && (
                        <UserListTab
                            isGuest={false}
                            recordListRoleFilters={recordListRoleFilters}
                            setRecordListRoleFilters={setRecordListRoleFilters}
                            currentTab={currentTab}
                            resource={resource}
                            onQueryChange={onQueryChange}
                            onDeleteUser={onDeleteUser}
                            transformRows={hideDeletingRows}
                            isSmallScreen={isSmallScreen}
                            query={query}
                            apiRef={staffApiRef}
                            lastUpdate={lastUpdate}
                            syncActive={syncActive}
                            headerActionsEl={hasPerm(resource, 'export') ? headerActionsEl : null}
                            columns={staffColumns}
                            columnVisibilityModel={staffColumnVisibility}
                            onColumnVisibilityModelChange={onStaffColumnVisibilityModelChange}
                            roleMap={roleMap}
                            hasPerm={hasPerm}
                            finalizeLogin={finalizeLogin}
                            roleCellBackgroundColor={roleCellBackgroundColor}
                        />
                    )}
                    {currentTab === 2 && (
                        <AccessGroupsPage setNavigateBackBarData={setNavigateBackBarData} />
                    )}
                </Grid>

            </Grid>
        </div >
    );
});

export default UserList;